import { Button, Col, Form, Row, Switch, TimePicker } from "antd";
import React from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

import { SelectOption } from "../../../libs/types";
import { InputField, SelectField } from "../../atoms/FormElement";
import { InputNumberField } from "../../atoms/FormElement/InputNumberField";

import { AmenityIconUpload } from "./AmenityIconUpload";
import { findOverlappingSlot, formatTimeValue, isValidTimeRange } from "./timeSlotUtils";
import {
  CatalogueId,
  FormType,
  MarketingConfigurationFormData,
  ViewingTimeSlotItem,
  isAmenityCatalogue,
  isIconUploadCatalogue,
} from "./types";

export type MarketingConfigurationFormProps = {
  methods: UseFormReturn<MarketingConfigurationFormData>;
  formType: FormType;
  catalogueId: CatalogueId;
  organizationOptions: SelectOption[];
  existingTimeSlots?: ViewingTimeSlotItem[];
  editingUid?: string | null;
  onSubmit: (data: MarketingConfigurationFormData) => void;
  onCancel: () => void;
};

export const MarketingConfigurationForm = (props: MarketingConfigurationFormProps) => {
  const {
    methods,
    formType,
    catalogueId,
    organizationOptions,
    existingTimeSlots = [],
    editingUid = null,
    onSubmit,
    onCancel,
  } = props;
  const { control, formState, handleSubmit, trigger } = methods;
  const { errors } = formState;

  const watchedStartTime = useWatch({ control, name: "startTime" });
  const watchedEndTime = useWatch({ control, name: "endTime" });
  const watchedOrganization = useWatch({ control, name: "organization" });

  const isAmenity = isAmenityCatalogue(catalogueId);
  const isViewingDay = catalogueId === "viewingDaysConfig";
  const isViewingTimeSlot = catalogueId === "viewingTimeSlotsConfig";
  const isFeedback = catalogueId === "feedbackQuestionnaireConfig";
  const isPublicListUrl = catalogueId === "marketingPublicListUrl";

  const validateTimeSlotRange = () => {
    const startTime = formatTimeValue(watchedStartTime);
    const endTime = formatTimeValue(watchedEndTime);

    if (!startTime || !endTime) return true;
    if (!isValidTimeRange(startTime, endTime)) {
      return "End time must be after start time";
    }

    const overlap = findOverlappingSlot(startTime, endTime, existingTimeSlots, editingUid);
    if (overlap) {
      return `Overlaps with ${overlap.startTime} - ${overlap.endTime}`;
    }

    return true;
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Row gutter={12}>
        <Col span={24}>
          <SelectField
            isRequired
            fieldname="organization"
            label="Organization"
            control={control}
            isDisabled
            selectOption={organizationOptions}
            rules={{ required: "This field is required" }}
            iProps={{ placeholder: "Select Organization" }}
            validate={errors?.organization && "error"}
            validMessage={errors?.organization?.message}
          />
        </Col>

        {(isAmenity || isViewingDay || isFeedback) && (
          <Col span={24}>
            <InputField
              isRequired
              fieldname="label"
              label="Label"
              control={control}
              rules={{ required: "This field is required" }}
              iProps={{ placeholder: "Enter label", disabled: isViewingDay && formType === "edit" }}
              validate={errors?.label && "error"}
              validMessage={errors?.label?.message}
            />
          </Col>
        )}

        {isPublicListUrl && (
          <Col span={24}>
            <InputField
              isRequired
              fieldname="url"
              label="Public List URL"
              control={control}
              rules={{ required: "This field is required" }}
              iProps={{ placeholder: "https://example.com/listings" }}
              validate={errors?.url && "error"}
              validMessage={errors?.url?.message}
            />
          </Col>
        )}

        {isIconUploadCatalogue(catalogueId) && (
          <Col span={24}>
            <Form.Item label="Icon">
              <Controller
                name="url"
                control={control}
                render={({ field }) => (
                  <AmenityIconUpload
                    value={field.value}
                    onChange={field.onChange}
                    catalogueId={catalogueId}
                    organizationName={watchedOrganization?.label}
                  />
                )}
              />
            </Form.Item>
          </Col>
        )}

        {isViewingTimeSlot && (
          <>
            <Col span={12}>
              <Form.Item
                required
                label="Start Time"
                validateStatus={errors?.startTime && "error"}
                help={errors?.startTime?.message}
              >
                <Controller
                  name="startTime"
                  control={control}
                  rules={{
                    required: "This field is required",
                    validate: validateTimeSlotRange,
                  }}
                  render={({ field }) => (
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        void trigger("endTime");
                      }}
                      placeholder="Select start time"
                    />
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                required
                label="End Time"
                validateStatus={errors?.endTime && "error"}
                help={errors?.endTime?.message}
              >
                <Controller
                  name="endTime"
                  control={control}
                  rules={{
                    required: "This field is required",
                    validate: validateTimeSlotRange,
                  }}
                  render={({ field }) => (
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        void trigger("startTime");
                      }}
                      placeholder="Select end time"
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}

        {isFeedback && (
          <Col span={24}>
            <InputNumberField
              isRequired
              fieldname="order"
              label="Order"
              control={control}
              rules={{ required: "This field is required" }}
              inputNumberProps={{ placeholder: "Order", min: 1 }}
            />
          </Col>
        )}

        {!isPublicListUrl && (
          <Col span={24}>
            <Form.Item label="Status" style={{ marginBottom: 24 }}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Switch className="switch" checked={field.value} onChange={field.onChange} />
                    <span>{field.value ? "Active" : "Inactive"}</span>
                  </div>
                )}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={24} style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit">
            {formType === "add" ? "Add" : "Save"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
